import React, { useState, useEffect, useRef } from "react";

import "bootstrap/dist/css/bootstrap.min.css";
import { NodeType } from "../../constants";
import { NodeTemplateId } from "../../registry/types";
// Resolve a node type to its unversioned `<packageId>/<templateId>` form so
// comparisons against the NodeType enum (which is unversioned) line up even
// when the registry handed us the canonical `…@<major>` id. Without this,
// JS_COMPUTATION nodes route to the Python interpreter and the sandbox
// raises a SyntaxError on JS source.
import { unversionedNodeType } from "../../utils/flowNodeCanonicalType";

// Editor
import Editor, { Monaco } from "@monaco-editor/react";
import { useFlowContext } from "../../providers/FlowProvider";
import { shouldSaveOutputOnRun } from "../../utils/saveOutputDataset";
import { executionResultOutput } from "../../utils/executionResult";
import { runKeyWithShared, sharedWidgetsOf } from "../../utils/references/sharedParameters";
import { registerRunNodeAction } from "./runNodeMonacoAction";
import { MissingModuleNotice, type InstallState } from "./MissingModuleNotice";
import { MIN_PROGRESS_MS, readInstallResponse } from "../../utils/libraryInstall";
import { resolveNodeDisplayLabel } from "../../utils/palettePackageFactoryDraft";
import { useProvenanceContext } from "../../providers/ProvenanceProvider";
import { useCollab, CodeProposal } from "../../providers/CollaborationProvider";
import { useMonacoExternalValue } from "../../hook/useMonacoExternalValue";
import { CredentialHint } from "./CredentialHint";
import {
    credentialLiterals,
    findingsKey,
    firstHostInCode,
    type CredentialFinding,
} from "../../services/connectionKeys/credentialLiterals";
import { usePackageBackendRun } from "../../hook/usePackageBackendRun";
import { useGrammarInputState } from "../../hook/useGrammarInputState";
import { upstreamErroredMessage } from "../../utils/nodeEmptyState";
import { wiredInputSlots } from "../../utils/inputSlots";
import { ICodeData } from "../../types";
import { ReferenceStrip } from "./widgets/WidgetTag";
import { insertReference, useCodeReferences } from "./widgets/monacoCodeReferences";
import { useEditorTooltip } from "./useEditorTooltip";
import type { ChipPreviewSource } from "./widgets/chipPreviewSource";
import type { CodeLanguage, InputScope, ReferenceScope } from "../../utils/references/codeReferences";
import { NOTEBOOK_CODE_EDITOR_MAX, useNotebookEditorHeight } from "./useNotebookEditorHeight";
import { nodeEditorLook } from "./nodeEditorLook";
import { NODE_EDITOR_SCROLLING } from "./nodeEditorScrolling";

/** In a notebook cell the output takes its own height up to this, then scrolls. */
const NOTEBOOK_OUTPUT_MAX = 320;

const NO_REFERENCES: ReferenceScope = { widgets: [], inputs: [], shared: [] };
const NO_INPUTS: InputScope[] = [];

type CodeEditorProps = {
    setOutputCallback: any;
    data: any;
    output: ICodeData;
    nodeType: NodeTemplateId;
    replacedCode: string; // code with all marks resolved
    sendCodeToWidgets: any;
    replacedCodeDirty: boolean;
    readOnly: boolean;
    defaultValue?: any;
    floatCode?: any;
    /** #662: what the node's references name: its widgets and its inputs. */
    references?: ReferenceScope;
    /** The inputs whose tags sit above the editor, with the widgets'. */
    stripInputs?: InputScope[];
    /** Read an input's columns for its tags. */
    onLoadColumns?: (slot: number) => void;
    widgetLanguage?: CodeLanguage;
    /** What its chips' and tags' hovers preview (chipPreviewSource). */
    chipPreviews?: ChipPreviewSource | null;
    /** A run's code put back from the provenance drawer; each new one is
     *  written in, even when it is the code the editor loaded with. */
    restoredCode?: { code: string; nonce: number } | null;
};

/** dev/117: how long after the last keystroke the code is scanned for a credential literal. */
export const CREDENTIAL_SCAN_DEBOUNCE_MS = 300;
const CREDENTIAL_MARKER_OWNER = "curio-credential";

/** Best-effort Monaco warning markers for the findings; skipped when the
 * Monaco build (or the test fake) has no setModelMarkers. Never an error
 * squiggle: nothing is wrong with the syntax. */
function setCredentialMarkers(monaco: any, editor: any, findings: CredentialFinding[]): void {
    const setMarkers = monaco?.editor?.setModelMarkers;
    const model = editor?.getModel?.();
    if (typeof setMarkers !== "function" || !model) return;
    const severity = monaco?.MarkerSeverity?.Warning ?? 4;
    setMarkers.call(monaco.editor, model, CREDENTIAL_MARKER_OWNER, findings.map((f) => ({
        severity,
        message: "Looks like an API key — use a connection key (curio_secret) instead.",
        startLineNumber: f.line,
        startColumn: 1,
        endLineNumber: f.line,
        endColumn: 1e6,
    })));
}

/** Marks the ResizeObserver this module put on `window`. */
const DEFERS_TO_NEXT_FRAME = Symbol.for("curio.resizeObserverDefersToNextFrame");

/**
 * Once a code editor has mounted, every ResizeObserver runs its callback in the
 * animation frame after the resize, which keeps Monaco's automatic layout from
 * raising "ResizeObserver loop completed with undelivered notifications". The
 * wrapper goes on once: an editor that mounts later finds it there and leaves
 * it, so a callback runs one frame late however many editors the page holds
 * (#742).
 */
function deferResizeObserverCallbacks(): void {
    const Original: any = window.ResizeObserver;
    if (typeof Original !== "function" || Original[DEFERS_TO_NEXT_FRAME]) return;
    const Deferred: any = function (callback: ResizeObserverCallback) {
        return new Original((entries: ResizeObserverEntry[], observer: ResizeObserver) => {
            window.requestAnimationFrame(() => callback(entries, observer));
        });
    };
    Object.assign(Deferred, Original);
    Deferred[DEFERS_TO_NEXT_FRAME] = true;
    window.ResizeObserver = Deferred;
}

function CodeEditor({
    setOutputCallback,
    data,
    output,
    nodeType,
    replacedCode,
    sendCodeToWidgets,
    replacedCodeDirty,
    readOnly,
    defaultValue,
    floatCode,
    references = NO_REFERENCES,
    stripInputs = NO_INPUTS,
    onLoadColumns,
    widgetLanguage = "python",
    chipPreviews = null,
    restoredCode = null,
}: CodeEditorProps) {
    const [code, setCode] = useState<string>(""); // code with all original markers
    const [execCount, setExecCount] = useState<number>(0);
    // #662: the mounted editor, for the reference tags and chips.
    const [widgetEditor, setWidgetEditor] = useState<{ editor: any; monaco: any } | null>(null);
    useCodeReferences(widgetEditor?.editor, widgetEditor?.monaco, references, widgetLanguage);
    // Its hovers, Monaco's own being off, in the Curio tooltip.
    useEditorTooltip(widgetEditor?.editor, widgetEditor?.monaco, references, widgetLanguage, chipPreviews);
    // In a notebook cell the editor is as tall as its lines and the output
    // takes its own height below it.
    const editorHeight = useNotebookEditorHeight(NOTEBOOK_CODE_EDITOR_MAX);
    const inCell = editorHeight.inCell;

    const {
        workflowNameRef,
        markNodeExecuted,
        markNodeStale,
        markNodeErrored,
        signalNodeExecDone,
        projectId,
        defaultSaveOutputDataset,
        isDashboardSource,
        playNodesUpTo,
        nodes,
        edges,
        dashboardOn,
    } = useFlowContext();
    // The wired circles: the value on circle k is the code's input_k. A ref,
    // since the run effect below is keyed on the resolved code alone.
    const wiredSlotsRef = useRef<number[]>([]);
    wiredSlotsRef.current = wiredInputSlots(edges ?? [], data.nodeId);
    // The code in a plain gray box over its output, as in a notebook, except
    // on a dashboard tile.
    const look = nodeEditorLook(!!dashboardOn);
    const { nodeExecProv } = useProvenanceContext();
    const collab = useCollab();
    // dev/91: non-null exactly when this template declares a backendHandler.
    const backendRun = usePackageBackendRun(nodeType);
    // #603: whether a node wired into this one ran and failed, asked the way a
    // chart or a Data Pool asks it.
    const { upstreamErrored, erroredSourceIds } = useGrammarInputState(data.nodeId);
    const failedUpstreamName = (): string | null => {
        const failed = (nodes ?? []).find((n: any) => n?.id === erroredSourceIds[0]);
        if (!failed?.data) return null;
        try {
            return resolveNodeDisplayLabel(failed.data as any);
        } catch {
            return null;
        }
    };

    const replacedCodeDirtyBypass = useRef(false);
    const outputRef = useRef<HTMLDivElement>(null);

    // dev/117: a credential-shaped literal in the code gets a hint — never a
    // block. Typing is scanned after a short pause; content that arrives whole
    // (dataset drop, LLM apply, collab, provenance) is scanned at once.
    const hintLanguage = unversionedNodeType(nodeType) === NodeType.JS_COMPUTATION ? "javascript" : "python";
    const [credentialFindings, setCredentialFindings] = useState<CredentialFinding[]>([]);
    const [credentialHost, setCredentialHost] = useState<string | null>(null);
    const [dismissedFindingsKey, setDismissedFindingsKey] = useState<string | null>(null);
    const scanTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const mountedRef = useRef(true);
    const monacoRef = useRef<Monaco | null>(null);
    const editorInstanceRef = useRef<any>(null);
    const scanNow = (value: string) => {
        if (!mountedRef.current) return;
        const found = credentialLiterals(value, hintLanguage);
        setCredentialFindings(found);
        setCredentialHost(found.length ? firstHostInCode(value) : null);
    };
    const scheduleScan = (value: string) => {
        if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
        scanTimerRef.current = setTimeout(() => scanNow(value), CREDENTIAL_SCAN_DEBOUNCE_MS);
    };
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
            setCredentialMarkers(monacoRef.current, editorInstanceRef.current, []);
        };
    }, []);
    useEffect(() => {
        setCredentialMarkers(monacoRef.current, editorInstanceRef.current, credentialFindings);
    }, [credentialFindings]);
    const currentFindingsKey = findingsKey(credentialFindings);
    const showCredentialHint =
        credentialFindings.length > 0 && dismissedFindingsKey !== currentFindingsKey;

    // The Monaco model is the source of truth while the user types; `code`
    // only mirrors it. Content flows INTO the editor exclusively through this
    // hook: it applies `defaultValue` when a genuinely new external string
    // arrives (initial load, dataset drop, LLM apply, provenance navigation)
    // and ignores `undefined`/stale flips, so in-progress edits are never
    // clobbered and the cursor never jumps (dev/70). `baselineCodeRef` is the
    // canonical "as-loaded" code the collab blur-diff compares against.
    const { baselineRef: baselineCodeRef, applyValue, attachEditor } = useMonacoExternalValue({
        externalValue: defaultValue,
        onExternalApply: (value) => {
            setCode(value);
            sendCodeToWidgets(value); // will resolve markers for templated boxes
            scanNow(value); // dev/117: content that arrived whole is scanned at once
        },
    });

    // A run's code put back from the provenance drawer, through the same
    // channel as a collaborator's applied change, then handled as content that
    // arrived whole. A default that equals the loaded code never applies, and
    // putting back the first run must.
    useEffect(() => {
        if (!restoredCode) return;
        applyValue(restoredCode.code);
        setCode(restoredCode.code);
        sendCodeToWidgets(restoredCode.code);
        scanNow(restoredCode.code);
    }, [restoredCode]);
    const codeRef = useRef<string>("");
    const collabRef = useRef(collab);
    collabRef.current = collab;
    useEffect(() => {
        codeRef.current = code;
    }, [code]);

    // @ts-ignore
    const handleCodeChange = (value, event) => {
        setCode(value);
        markNodeStale(data.nodeId);
        scheduleScan(typeof value === "string" ? value : ""); // dev/117
    };

    // ------------------------------------------------------------------
    // Collaboration: code-change proposals + receiving applied changes
    // ------------------------------------------------------------------

    const proposeOnBlur = () => {
        const c = collabRef.current;
        if (!c.enabled || !c.connected) return;
        if (c.users.length <= 1) return; // No peers — nothing to propose to.
        const local = codeRef.current;
        const baseline = baselineCodeRef.current;
        if (local === baseline) return;
        c.requestCodeChange(data.nodeId, baseline, local, "code");
    };

    // Install state for the missing-library notice (#299). Held here rather
    // than in the notice: pressing Run while pip is still working clears
    // `output.missingModule`, and a child-owned state would unmount with the
    // request still in flight.
    const [installState, setInstallState] = useState<InstallState>({ kind: "idle" });
    // Modules already installed once in this session. A second failure naming
    // one of them is not an install problem, so the notice stops offering the
    // button and says what to do instead.
    const installedOnceRef = useRef<Set<string>>(new Set());

    const installMissingLibrary = async (distribution: string) => {
        setInstallState({ kind: "installing", distribution });
        const startedAt = Date.now();
        try {
            // Imported on the click, not at module load. `packagesApi`
            // re-exports the package registry bootstrap, which pulls in the
            // whole node-adapter graph (vega, autk) - none of which a code
            // node needs to render, and which does not survive jsdom. A
            // static import here made three unrelated test suites carry a
            // mock for a dependency they never use.
            const { packagesApi } = await import("../../services/packages");
            const data = await packagesApi.addLibrary("python", distribution);
            // pip's already-satisfied path returns in microseconds; hold the
            // in-flight state long enough to be seen, without adding any delay
            // to a slow install.
            const elapsed = Date.now() - startedAt;
            if (elapsed < MIN_PROGRESS_MS) {
                await new Promise((r) => window.setTimeout(r, MIN_PROGRESS_MS - elapsed));
            }
            installedOnceRef.current.add(distribution);
            setInstallState({
                kind: "done", distribution, verdict: readInstallResponse(data),
            });
        } catch (e: any) {
            setInstallState({
                kind: "failed", distribution, message: e?.message || String(e),
            });
        }
    };

    // A new run supersedes whatever the last one offered.
    useEffect(() => {
        if (output.code === "exec") setInstallState({ kind: "idle" });
    }, [output.code]);

    // onMount fires once, so the action reads the CURRENT play function through
    // a ref rather than capturing the first render's (#223).
    const runNodeRef = useRef<() => void>(() => {});
    runNodeRef.current = () => playNodesUpTo(data.nodeId);

    const handleEditorMount = (editor: any, monaco: Monaco) => {
        attachEditor(editor);
        monacoRef.current = monaco;
        editorInstanceRef.current = editor;
        setWidgetEditor({ editor, monaco });
        editorHeight.attach(editor, monaco);
        editor.onDidBlurEditorText(proposeOnBlur);
        // Ctrl/Cmd+Enter. Registered here rather than on the window because
        // Monaco owns the chord while the editor has focus — and already bound
        // it to "insert line below", which is what the keystroke did before.
        registerRunNodeAction(editor, monaco, () => runNodeRef.current);
    };

    // Apply remote-approved code changes to this node's editor.
    useEffect(() => {
        if (!collab.enabled) return;
        const unsub = collab.onRemote("code_change_applied", (payload) => {
            const prop = payload as CodeProposal;
            if (!prop || prop.nodeId !== data.nodeId || prop.kind !== "code") return;
            // Skip if WE were the proposer — our local code already matches.
            if (collab.currentUserId != null &&
                prop.proposed_by?.user_id === collab.currentUserId) {
                baselineCodeRef.current = prop.newValue;
                return;
            }
            applyValue(prop.newValue); // editor + baseline (cursor-preserving)
            setCode(prop.newValue);
        });
        return unsub;
    }, [collab.enabled, collab.onRemote, collab.currentUserId, data.nodeId]);

    const pendingProposal = collab.proposals.find(
        (p) => p.nodeId === data.nodeId && p.kind === "code",
    );
    const proposalIsMine = Boolean(
        pendingProposal && collab.currentUserId != null &&
        pendingProposal.proposed_by?.user_id === collab.currentUserId,
    );

    useEffect(() => {
        if (floatCode != undefined) floatCode(code);
    }, [code]);

    useEffect(() => {
        if (output.code === "success" || output.code === "error") {
            setExecCount(prev => prev + 1);
        }
    }, [output.code]);

    /** A run's reply on the node; a success carries *runKey*, the key the run was sent with. */
    const processExecutionResult = (result: any, runKey: string) => {
        // The same rule a step of a run on the server goes through (useServerRun).
        const { shown, artifact } = executionResultOutput(result);
        setOutputCallback(shown.code === "success" ? { ...shown, runKey } : shown);
        if (artifact) {
            // outputCallback → applyNewOutput, which centrally auto-installs +
            // surfaces the produced dataset (no manual disk-icon save needed).
            data.outputCallback(data.nodeId, artifact);
            markNodeExecuted(data.nodeId);
        } else {
            // No artifact, so deliberately no outputCallback - nothing is
            // propagated downstream. That left every downstream node unable to
            // tell this apart from "never run", so it advised running the node
            // the user had just watched fail (#347). Record the failure instead,
            // and tell the runner, which then runs nothing below this node (#603).
            markNodeErrored(data.nodeId);
            signalNodeExecDone(data.nodeId, { failed: true });
        }
    };

    /** A run that ended without an output: shown, recorded and reported alike. */
    const failRun = (content: string) => {
        setOutputCallback({ code: "error", content });
        markNodeErrored(data.nodeId);
        signalNodeExecDone(data.nodeId, { failed: true });
    };

    // marks were resolved and new code is available
    useEffect(() => {
        if (!replacedCodeDirtyBypass.current) {
            replacedCodeDirtyBypass.current = true;
            return;
        }
        if (output.code !== "exec") return;
        if (replacedCode === "") {
            failRun("No code to execute");
            return;
        }
        // #603: a node fed by one that failed has nothing to run on. It says
        // which node failed and is not sent to the sandbox, where it could only
        // fail again with a message about missing input.
        if (upstreamErrored) {
            failRun(upstreamErroredMessage(failedUpstreamName()));
            return;
        }
        // What the run is sent with. Its result is stamped with this key, so a
        // selection, widget value or code changed while it runs leaves the
        // node stale.
        const runKey = runKeyWithShared(code, data.widgets, sharedWidgetsOf(nodes ?? []), data.selections);
        if (backendRun) {
            // dev/91: a backendHandler template Runs through the package
            // backend sandbox — registry-driven dispatch, never another
            // hardcoded template-id literal.
            void backendRun({ content: replacedCode, input: data.input }).then((outcome) => {
                if (outcome.ok) {
                    setOutputCallback({ code: "success", content: outcome.content, runKey });
                    markNodeExecuted(data.nodeId);
                } else {
                    failRun(outcome.content);
                }
            });
            return;
        }
        const isJsNode = unversionedNodeType(nodeType) === NodeType.JS_COMPUTATION;
        const interpreter = (isJsNode && data.jsInterpreter)
            ? data.jsInterpreter
            : data.pythonInterpreter;
        interpreter.interpretCode(
            code,
            replacedCode,
            data.input,
            data.inputTypes,
            (result: any) => processExecutionResult(result, runKey),
            nodeType,
            data.nodeId,
            workflowNameRef.current,
            nodeExecProv,
            projectId,
            // A node feeding a pinned dashboard tile saves its output whatever
            // its own toggle says: that saved dataset is what lets the tile draw
            // when someone opens the dashboard later.
            shouldSaveOutputOnRun(
                data, defaultSaveOutputDataset, isDashboardSource(data.nodeId),
            ),
            resolveNodeDisplayLabel(data),
            wiredSlotsRef.current,
        );
    }, [replacedCodeDirty]);

    // Confine drag-selections that start in the output box to the box itself.
    // We drive the selection manually on every mousemove (anchor at the initial
    // click, focus at the cursor clamped to the box's bounding rect), which
    // overrides the browser's native selection extension so the range never
    // bleeds into other nodes.
    useEffect(() => {
        const el = outputRef.current;
        if (!el) return;

        const caretFromPoint = (x: number, y: number): { node: Node; offset: number } | null => {
            const doc: any = document;
            if (typeof doc.caretRangeFromPoint === "function") {
                const r: Range | null = doc.caretRangeFromPoint(x, y);
                return r ? { node: r.startContainer, offset: r.startOffset } : null;
            }
            if (typeof doc.caretPositionFromPoint === "function") {
                const p = doc.caretPositionFromPoint(x, y);
                return p ? { node: p.offsetNode, offset: p.offset } : null;
            }
            return null;
        };

        const onMouseDown = (e: MouseEvent) => {
            if (e.button !== 0) return;
            if (!(e.target instanceof Node) || !el.contains(e.target)) return;
            // The second press of a double-click (and the third of a triple)
            // is the browser's: it selects the word, or the line, and that
            // cannot leave the box. Driving it here cancelled it.
            if (e.detail > 1) return;

            const anchor = caretFromPoint(e.clientX, e.clientY);
            if (!anchor || !el.contains(anchor.node)) return;

            // Stop the browser from starting its own drag-select; we drive
            // the selection manually so it can't extend outside the box.
            e.preventDefault();

            const sel = window.getSelection();
            sel?.removeAllRanges();
            sel?.setBaseAndExtent(anchor.node, anchor.offset, anchor.node, anchor.offset);

            const onMouseMove = (ev: MouseEvent) => {
                ev.preventDefault();
                const rect = el.getBoundingClientRect();
                const cx = Math.max(rect.left + 1, Math.min(rect.right - 1, ev.clientX));
                const cy = Math.max(rect.top + 1, Math.min(rect.bottom - 1, ev.clientY));
                const focus = caretFromPoint(cx, cy);
                if (!focus || !el.contains(focus.node)) return;
                window.getSelection()?.setBaseAndExtent(anchor.node, anchor.offset, focus.node, focus.offset);
            };

            const onMouseUp = () => {
                document.removeEventListener("mousemove", onMouseMove, true);
                document.removeEventListener("mouseup", onMouseUp, true);
            };

            document.addEventListener("mousemove", onMouseMove, true);
            document.addEventListener("mouseup", onMouseUp, true);
        };

        el.addEventListener("mousedown", onMouseDown);
        return () => {
            el.removeEventListener("mousedown", onMouseDown);
        };
    }, []);

    useEffect(deferResizeObserverCallbacks, []);

    const execLabel = output.code === "exec" ? "[*]:" : execCount > 0 ? `[${execCount}]:` : "[ ]:";
    const outputText = output.code === "exec"
        ? "Running..."
        : typeof output.content === "string" && output.content
            ? output.content
            : "No output yet";

    return (
        <div className="nowheel nodrag" style={{ height: inCell ? "auto" : "100%", display: "flex", flexDirection: "column", backgroundColor: "#fff", userSelect: "none" }}>
            {showCredentialHint ? (
                <CredentialHint
                    findings={credentialFindings}
                    readOnly={readOnly}
                    host={credentialHost}
                    onDismiss={() => setDismissedFindingsKey(currentFindingsKey)}
                />
            ) : null}
            {pendingProposal && (
                <div
                    style={{
                        padding: "4px 8px",
                        background: proposalIsMine ? "#fff8e1" : "#e3f2fd",
                        borderBottom: "1px solid #ccc",
                        fontSize: 11,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                    }}
                >
                    <span style={{ flex: 1 }}>
                        {proposalIsMine
                            ? `Awaiting approval from peers (${pendingProposal.approvals.length} so far).`
                            : `${pendingProposal.proposed_by?.username || "Peer"} proposed a code change.`}
                    </span>
                    {!proposalIsMine && (
                        <>
                            <button
                                type="button"
                                onClick={() => collab.approveCodeChange(pendingProposal.id)}
                                style={{ padding: "1px 8px", fontSize: 11 }}
                            >
                                Approve
                            </button>
                            <button
                                type="button"
                                onClick={() => collab.rejectCodeChange(pendingProposal.id)}
                                style={{ padding: "1px 8px", fontSize: 11 }}
                            >
                                Reject
                            </button>
                        </>
                    )}
                </div>
            )}
            <ReferenceStrip
                nodeId={data?.nodeId}
                widgets={references.widgets}
                inputs={stripInputs}
                shared={references.shared}
                selections={references.selections}
                language={widgetLanguage}
                disabled={readOnly}
                onInsert={(inner) => insertReference(widgetEditor?.editor, inner)}
                onLoadColumns={onLoadColumns}
                previews={chipPreviews}
            />
            <div className={look.wrapperClassName} style={{ flex: 2, minHeight: 0, ...editorHeight.wrapperStyle }}>
                {/* Uncontrolled on purpose: a per-keystroke `value` round-trip
                    lets a render that lands with a stale string do a full-model
                    replace — dropping characters and throwing the cursor to the
                    end (dev/70). External content arrives via attachEditor /
                    applyValue in useMonacoExternalValue instead. */}
                <Editor
                    height="100%"
                    language={unversionedNodeType(nodeType) === NodeType.JS_COMPUTATION ? "javascript" : "python"}
                    theme="vs"
                    onChange={handleCodeChange}
                    onMount={handleEditorMount}
                    options={{
                        // @ts-ignore
                        inlineSuggest: true,
                        fontSize: 13,
                        fontFamily: "'Source Code Pro', Consolas, 'Courier New', monospace",
                        formatOnType: true,
                        autoClosingBrackets: "always",
                        minimap: { enabled: false },
                        readOnly: readOnly,
                        scrollBeyondLastLine: false,
                        // As a Jupyter cell scrolls: never wrapped, and each
                        // bar at rest whenever the code runs past its side.
                        ...NODE_EDITOR_SCROLLING,
                        // A plain input box, but on a dashboard tile.
                        ...look.editorOptions,
                        // Monaco draws its hover in the node, where the canvas's
                        // zoom scales it: the Curio tooltip shows it instead
                        // (useEditorTooltip), on a dashboard tile too.
                        hover: { enabled: false },
                    }}
                />
            </div>
            <div
                ref={outputRef}
                // `nopan` as well as `nodrag`: in a node that cannot be
                // dragged (a read-only dataflow), react-flow pans the canvas
                // on a press and zooms it on a double-click unless told not to.
                className="nowheel nodrag nopan"
                // The wrapper above carries the same class, so a ".nowheel.nodrag"
                // lookup resolves to it first and picks up Monaco's rendered code
                // lines. Tests read the result through this attribute instead.
                data-curio-node-output="true"
                style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    // The output is plain text under the input box, with no
                    // rule above it; a dashboard tile keeps its gray box.
                    backgroundColor: dashboardOn ? "#f7f7f7" : "#ffffff",
                    borderTop: dashboardOn ? "1px solid #e0e0e0" : "none",
                    // A failed node's error line (NodeOutcomeStrip) sits over
                    // the bottom of this box: room below the last line lets it
                    // scroll clear of the strip. In a notebook cell the strip
                    // sits under the box instead.
                    padding: output.code === "error" && !inCell ? "4px 8px 32px" : "4px 8px",
                    // In a notebook cell the output takes its own height.
                    ...(inCell ? { flex: "none", maxHeight: `${NOTEBOOK_OUTPUT_MAX}px`, padding: "6px 8px 0" } : {}),
                    fontSize: "11px",
                    fontFamily: "'Source Code Pro', Consolas, 'Courier New', monospace",
                    whiteSpace: "pre-wrap",
                    color: output.code === "error" ? "#c0392b" : "#333",
                    userSelect: "text",
                    cursor: "text",
                }}
            >
                <span style={{ color: "#303F9F", fontWeight: "bold", marginRight: "6px" }}>{execLabel}</span>
                {outputText}
                {output.missingModule ? (
                    <MissingModuleNotice
                        notice={output.missingModule}
                        state={installState}
                        retried={
                            !!output.missingModule.distribution
                            && installedOnceRef.current.has(output.missingModule.distribution)
                            && installState.kind === "idle"
                        }
                        onInstall={(d) => void installMissingLibrary(d)}
                        onRunNode={() => runNodeRef.current()}
                    />
                ) : null}
            </div>
        </div>
    );
}

export default CodeEditor;
